/**
 * La casilla «Acepto los Términos…» que va delante de pagar (candidato), de
 * enviar la solicitud (socio) y de registrar la agencia o pedir acceso.
 *
 * Es OBLIGATORIA del lado del servidor (400 `terms_required` /
 * `terms_outdated`, ver `~/lib/termsVersions`): esta pieza sólo la pinta. El
 * enlace va DENTRO de la etiqueta a propósito: el nombre accesible de la
 * casilla es la frase entera, y por la regla del HTML hacer clic en el enlace
 * abre el documento sin marcar la casilla.
 *
 * `notice` es el aviso de «actualizamos los términos» (la versión cambió y hay
 * que volver a marcar); `error`, el de «marca la casilla para continuar».
 */
interface Props {
  id: string;
  checked: boolean;
  onChange: (checked: boolean) => void;
  before: string;
  label: string;
  href: string;
  after: string;
  notice?: string | null;
  error?: string | null;
  disabled?: boolean;
  className?: string;
}

export default function TermsCheckbox({
  id,
  checked,
  onChange,
  before,
  label,
  href,
  after,
  notice,
  error,
  disabled = false,
  className,
}: Props) {
  const describedBy = error ? `${id}-error` : notice ? `${id}-notice` : undefined;
  return (
    <div className={className ? `space-y-1 ${className}` : 'space-y-1'}>
      <label htmlFor={id} className="flex items-start gap-2 text-sm cursor-pointer">
        <input
          id={id}
          type="checkbox"
          className="checkbox checkbox-primary checkbox-sm mt-0.5"
          checked={checked}
          disabled={disabled}
          // aria-required y no `required`: el navegador frenaría el envío con su
          // propio globo y el aviso de acá (el mismo en todas las pantallas) no saldría.
          aria-required
          aria-invalid={error ? true : undefined}
          aria-describedby={describedBy}
          onChange={(e) => onChange(e.target.checked)}
        />
        <span>
          {before}
          <a href={href} target="_blank" rel="noopener noreferrer" className="link">
            {label}
          </a>
          {after}
        </span>
      </label>
      {notice && (
        <p id={`${id}-notice`} className="text-xs" role="status">
          {notice}
        </p>
      )}
      {error && (
        <p id={`${id}-error`} className="text-xs" style={{ color: 'var(--coral-ink)' }} role="alert">
          {error}
        </p>
      )}
    </div>
  );
}
